import React, { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import styled, { css } from 'styled-components';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces, typography } from '@99designs/emu/settings';

interface StatProps {
    score: number;
    color: string;
    direction?: 'horizontal' | 'vertical';
}

const ANIMATION_DURATION_IN_SEC = 1;

const Wrapper = styled(Box)<{ direction?: string }>`
    position: relative;

    ${({ direction }) =>
        direction === 'horizontal' &&
        css`
            display: grid;
            grid-template-columns: 80% 20%;
            grid-template-areas: 'stat-container stat-score';
        `}

    ${({ direction }) =>
        direction === 'vertical' &&
        css`
            height: 100%;
        `}
`;

const OuterBox = styled(Box)<{ direction?: string }>`
    position: relative;
    border-style: solid;
    border-width: 1px;
    border-radius: 29px;

    ${({ direction }) =>
        direction === 'horizontal' &&
        css`
            grid-area: stat-container;
            height: ${spaces['4bu']};
        `}

    ${({ direction }) =>
        direction === 'vertical' &&
        css`
            padding-bottom: ${spaces['0.75bu']};
            margin: 0 auto;
            width: ${spaces['3bu']};
            height: 100%;
        `}
`;

const BarChartContainer = styled(Box)<{ direction?: string }>`
    position: relative;
    border-radius: ${spaces['1.5bu']};

    ${({ direction }) =>
        direction === 'horizontal' &&
        css`
            grid-area: stat-container;
            height: 40px;
            margin: 9px;
        `}

    ${({ direction }) =>
        direction === 'vertical' &&
        css`
            height: 100%;
            margin: ${spaces['0.25bu']};
        `}
`;

const ResultsBar = styled(Box)<{
    color: string;
    percentage: number;
    direction?: string;
    score: number;
}>`
    position: absolute;
    background: ${({ color }) => color};

    ${({ direction, percentage }) =>
        direction === 'horizontal' &&
        css`
            width: ${percentage}%;
            height: 40px;
            transition: width ${ANIMATION_DURATION_IN_SEC}s;
            border-radius: ${spaces['1.5bu']};
        `}

    // for vertical bar charts if score < 20 => height of the bar is less than 20 so the border radius of 20px 
    // makes the shape rectange so using 100% is score is < 20.
    ${({ direction, percentage, score }) =>
        direction === 'vertical' &&
        css`
            width: 100%;
            height: ${percentage}%;
            bottom: 0;
            transition: height ${ANIMATION_DURATION_IN_SEC}s;
            border-radius: ${score < 20 ? '100%' : spaces['1.5bu']};
        `}
`;

const Score = styled.span`
    grid-area: stat-score;
    text-align: left;
    font-family: ${typography.fontFamilies.attribution};
    font-style: italic;
    position: absolute;
    padding-left: ${spaces['1.5bu']};
    ${mediaQuery.minimum('normal')} {
        font-size: ${typography.typeScale.size9}px;
        top: -20px;
    }
    ${mediaQuery.between('small', 'medium')} {
        font-size: ${typography.typeScale.size7}px;
        top: -5px;
    }
`;

export default function StatBarChart({ score, color, direction = 'horizontal' }: StatProps) {
    if (score < 0 || score > 100) {
        score = 0; // Invalid score, set to 0;
    }
    const [percentage, setPercentage] = useState(0);

    const handleEntry = () => {
        setPercentage(score);
    };

    const handleExit = () => {
        setPercentage(0);
    };

    return (
        <Waypoint onEnter={handleEntry} onLeave={handleExit} topOffset={'10%'} bottomOffset={'5%'}>
            <Wrapper direction={direction}>
                <OuterBox direction={direction}>
                    <BarChartContainer direction={direction}>
                        <ResultsBar
                            color={color}
                            percentage={percentage}
                            direction={direction}
                            score={score}
                        />
                    </BarChartContainer>
                </OuterBox>
                {direction === 'horizontal' && <Score>{score}</Score>}
            </Wrapper>
        </Waypoint>
    );
}
